import { isPermitted } from '../../../../components/permission-control/permission-control';

const normalizePermissionName = (permissionName) => permissionName?.toLowerCase() ?? '';

/**
 * CBAC any-of check: true when at least one of `permissionNames` is granted.
 * Mirrors the backend any-of permission sets used on case-plan routes.
 *
 * @param {boolean} cbacConfigFlag - When false, `isPermitted` grants everything.
 * @param {Array<{permissionName: string, permissionId: number}>} permissions - Permission catalog from global state.
 * @param {string[]} permissionNames - Accepted permission slugs, canonical first.
 * @param {number[]} permissionIds - Permission ids granted to the staff member.
 * @returns {boolean} True when any listed permission is granted.
 */
export const isAnyPermitted = (cbacConfigFlag, permissions, permissionNames, permissionIds) =>
  (permissionNames ?? []).some((permissionName) =>
    isPermitted(cbacConfigFlag, permissions, permissionName, permissionIds),
  );

const hasGrantedPermissionPrefix = (permissions, permissionIds, prefix) =>
  permissions?.some(
    (permission) =>
      normalizePermissionName(permission.permissionName).startsWith(prefix) &&
      permissionIds?.includes(permission.permissionId),
  ) === true;

/**
 * CBAC prefix check: true when a granted permission name starts with `prefix`.
 * When CBAC is off, matches `isPermitted` and grants everything.
 *
 * @param {boolean} cbacConfigFlag - When false, every prefix is treated as granted.
 * @param {Array<{permissionName: string, permissionId: number}>} permissions - Permission catalog from global state.
 * @param {string} prefix - Permission name prefix, e.g. `case_plan_reentry`.
 * @param {number[]} permissionIds - Permission ids granted to the staff member.
 * @returns {boolean} True when a granted permission matches the prefix.
 */
export const isPermissionPrefixPermitted = (cbacConfigFlag, permissions, prefix, permissionIds) => {
  if (!cbacConfigFlag) {
    return true;
  }

  if (!prefix) {
    return false;
  }

  return hasGrantedPermissionPrefix(permissions, permissionIds, prefix.toLowerCase());
};
